import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { FaChurch, FaHandHoldingHeart, FaMoneyBillWave } from "react-icons/fa";
import { useCampuses } from "../../contexts/CampusContext";
import { usePayment } from "../../contexts/PaymentContext";
import PageHeader from '../common/PageHeader';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 10
    }
  }
};

const cardHover = {
  hover: {
    y: -5,
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
    transition: {
      duration: 0.3
    }
  }
};

export default function Giving() {
  const { 
    initializePayment, 
    redirectToPayFast, 
    isProcessing, 
    paymentError 
  } = usePayment();
  const { campuses } = useCampuses();
  
  const [selectedCampus, setSelectedCampus] = useState(null);
  const [formError, setFormError] = useState("");

  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    offeringType: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    campus_code: "",
    paymentMethod: "", 
  });

  useEffect(() => {
    if (selectedCampus) {
      setPaymentForm(prev => ({
        ...prev,
        campus_code: selectedCampus.campus_code,
      }));
    }
  }, [selectedCampus]);

  const offeringTypes = ["Tithe", "Sacrifice", "Seed", "Pledge", "General giving"];
  
  const paymentMethods = [
    { 
      code: "cc", 
      name: "Credit Card", 
      image: "https://t4.ftcdn.net/jpg/04/06/75/39/360_F_406753914_SFSBhjhp6kbHblNiUFZ1MXHcuEKe7e7P.jpg" 
    },
    { 
      code: "eft", 
      name: "EFT", 
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQw421zgBdJQcFRLsEhpn8fI0svz0QJ3kAz1c2_M1GwgywUtFGb2VZMNTA09r6bYT5Rzn0&usqp=CAU" 
    },
    { 
      code: "dc", 
      name: "Debit Card", 
      image: "https://t4.ftcdn.net/jpg/04/06/75/39/360_F_406753914_SFSBhjhp6kbHblNiUFZ1MXHcuEKe7e7P.jpg" 
    },
    { 
      code: "mp", 
      name: "Scan to Pay", 
      image: "https://cdn.prod.website-files.com/60b64279ca5123444fed1f79/61a9f0805fb3b20012646682_Masterpass%20Scan%20to%20Pay%20Logo.%20PNG.png" 
    },
    { 
      code: "zp", 
      name: "Zapper", 
      image: "https://i0.wp.com/www.labelyourlife.co.za/wp/wp-content/uploads/2021/01/lyl_zapper.png?resize=1024%2C288&ssl=1" 
    },
    { 
      code: "ss", 
      name: "Snap Scan", 
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTMexSEFQmCdODozYLDclSRi16Pugbxk_0f3Q&s" 
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "campus_code" && value !== "") {
      const selectedIndex = campuses.findIndex(campus => campus.campus_code === value);
      if (selectedIndex !== -1) {
        setSelectedCampus(campuses[selectedIndex]);
      }
    }
    
    setPaymentForm({
      ...paymentForm,
      [name]: value,
    });
  };

  const handlePaymentMethodChange = (methodCode) => {
    setPaymentForm({
      ...paymentForm,
      paymentMethod: methodCode,
    });
  };

  const validateForm = () => {
    if (!paymentForm.offeringType) {
      setFormError("Please select an offering type");
      return false;
    }

    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      setFormError("Please enter a valid amount");
      return false;
    }
    
    if (!paymentForm.firstName || !paymentForm.lastName || !paymentForm.email) {
      setFormError("Please fill in all required fields");
      return false;
    }
    
    if (paymentForm.email && !paymentForm.email.includes('@')) {
      setFormError("Please enter a valid email address");
      return false;
    }

    if (!paymentForm.campus_code) {
      setFormError("Please select a campus");
      return false;
    }

    if (!paymentForm.paymentMethod) {
      setFormError("Please select a payment method");
      return false;
    }
    
    setFormError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const paymentResponse = await initializePayment({
        amount: parseFloat(paymentForm.amount),
        offeringType: paymentForm.offeringType,
        firstName: paymentForm.firstName,
        lastName: paymentForm.lastName,
        email: paymentForm.email,
        phone: paymentForm.phone,
        campus_code: paymentForm.campus_code,
        paymentMethod: paymentForm.paymentMethod,
      });
      
      if (paymentResponse.success) {
        redirectToPayFast(paymentResponse);
      } else {
        setFormError(paymentResponse.error || "Payment initialization failed");
      }
      
    } catch (error) {
      console.error("Payment initiation failed:", error);
      setFormError("Failed to initiate payment. Please try again later.");
    }
  };

  return (
    <motion.main 
      className="w-full relative"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <PageHeader 
        title="Giving" 
        subtitle="See what God can do through your generosity"
        imageUrl="https://media.istockphoto.com/id/1080150772/photo/girl-in-a-light-sweater-gives-a-knitted-heart.jpg?s=170667a&w=0&k=20&c=CZ1l_QKyXrhsL-kzzswbvt_vmhsPbxkH3C6PJmqymyw="
        imageAlt="Giving"
      />

      <motion.section 
        className="relative z-20 bg-white -mt-8 pt-8 px-4 pb-8 shadow-lg"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="grid md:grid-cols-2 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.section variants={itemVariants}>
              <h3 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                <FaHandHoldingHeart className="mr-3 text-primary" />
                Offering Types
              </h3>
              <div className="flex flex-wrap gap-3 mb-8">
                {offeringTypes.map((type) => (
                  <motion.span 
                    key={type}
                    className="px-4 py-2 bg-gray-100 rounded-full text-gray-800 font-medium"
                    whileHover={{ scale: 1.05 }}
                  >
                    {type}
                  </motion.span>
                ))}
              </div>

              <motion.div 
                className="space-y-6"
                variants={containerVariants}
              >
                <motion.div 
                  className="bg-white p-6 rounded-xl shadow-md border border-gray-100"
                  variants={itemVariants}
                  whileHover="hover"
                  variants={cardHover}
                >
                  <h3 className="text-xl font-semibold mb-4 flex items-center">
                    <FaChurch className="mr-2 text-primary" />
                    Banking Details
                  </h3>
                  <div className="space-y-4">
                    <motion.div 
                      className="bg-gray-50 p-5 rounded-lg border-l-4 border-primary"
                      whileHover={{ x: 5 }}
                    >
                      <h4 className="font-bold text-gray-800 mb-2">Nedbank</h4>
                      <ul className="space-y-2">
                        <li><span className="font-medium">Account No:</span> 1160908397</li>
                        <li><span className="font-medium">Branch Code:</span> 142936</li>
                        <li><span className="font-medium">Reference:</span> [Offering Type][Your Name][Membership No (Optional)]</li>
                      </ul>
                    </motion.div>
                    <motion.div 
                      className="bg-gray-50 p-5 rounded-lg border-l-4 border-primary"
                      whileHover={{ x: 5 }}
                    >
                      <h4 className="font-bold text-gray-800 mb-2">FNB</h4>
                      <ul className="space-y-2">
                        <li><span className="font-medium">Account No:</span> 62748584994</li>
                        <li><span className="font-medium">Branch Code:</span> 142359</li>
                        <li><span className="font-medium">Reference:</span> [Offering Type][Your Name][Membership No (Optional)]</li>
                      </ul>
                    </motion.div>
                    <motion.div 
                      className="bg-gray-50 p-5 rounded-lg border-l-4 border-primary"
                      whileHover={{ x: 5 }}
                    >
                      <h4 className="font-bold text-gray-800 mb-2">Standard Bank</h4>
                      <ul className="space-y-2">
                        <li><span className="font-medium">Account No:</span> 242363679</li>
                        <li><span className="font-medium">Branch Code:</span> 142222</li>
                        <li><span className="font-medium">Reference:</span> [Offering Type][Your Name][Membership No (Optional)]</li>
                      </ul>
                    </motion.div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.section>

            <motion.section variants={itemVariants}>
              <motion.div 
                className="bg-white p-8 rounded-xl shadow-md border border-gray-100"
                whileHover="hover"
                variants={cardHover}
              >
                <h3 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                  <FaMoneyBillWave className="mr-3 text-primary" />
                  Make an Online Offering
                </h3>
                
                <AnimatePresence>
                  {(formError || paymentError) && (
                    <motion.div 
                      className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg"
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                    >
                      {formError || paymentError}
                    </motion.div>
                  )}
                </AnimatePresence>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <motion.div variants={itemVariants}>
                    <label className="block text-gray-700 text-sm font-medium mb-2">Offering Type</label>
                    <select
                      name="offeringType"
                      value={paymentForm.offeringType}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      required
                    >
                      <option value="">Choose an offering type</option>
                      {offeringTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </motion.div>
                  
                  <motion.div variants={itemVariants}>
                    <label className="block text-gray-700 text-sm font-medium mb-2">Amount (ZAR)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">R</span>
                      <input
                        type="number"
                        name="amount"
                        value={paymentForm.amount}
                        onChange={handleChange}
                        placeholder="Enter amount"
                        min="1"
                        step="0.01"
                        className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        required
                      />
                    </div>
                  </motion.div>
                  
                  <motion.div className="grid grid-cols-2 gap-4" variants={itemVariants}>
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2">First Name</label>
                      <input
                        type="text"
                        name="firstName"
                        value={paymentForm.firstName}
                        onChange={handleChange}
                        placeholder="Your first name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2">Last Name</label>
                      <input
                        type="text"
                        name="lastName"
                        value={paymentForm.lastName}
                        onChange={handleChange}
                        placeholder="Your last name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        required
                      />
                    </div>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <label className="block text-gray-700 text-sm font-medium mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={paymentForm.phone}
                      onChange={handleChange}
                      placeholder="Your phone number"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      required
                    />
                  </motion.div>
                   
                  <motion.div variants={itemVariants}>
                    <label className="block text-gray-700 text-sm font-medium mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={paymentForm.email}
                      onChange={handleChange}
                      placeholder="Your email address"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      required
                    />
                  </motion.div>
                  
                  <motion.div variants={itemVariants}>
                    <label className="block text-gray-700 text-sm font-medium mb-2">Campus</label>
                    <select
                      name="campus_code"
                      value={paymentForm.campus_code}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      required
                    >
                      <option value="">Select your campus</option>
                      {campuses && campuses.map((campus) => (
                        <option key={campus.campus_code} value={campus.campus_code}>
                          {campus.name}
                        </option>
                      ))}
                    </select>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                  <label className="block text-gray-700 text-sm font-medium mb-3">Payment Method</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {paymentMethods.map((method) => (
                      <motion.div 
                        key={method.code}
                        className={`relative border-2 rounded-lg p-3 flex flex-col items-center cursor-pointer transition-all ${
                          paymentForm.paymentMethod === method.code 
                            ? 'border-primary bg-primary/10' 
                            : 'border-gray-200 hover:border-primary/50'
                        }`}
                        onClick={() => handlePaymentMethodChange(method.code)}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <input
                          type="radio"
                          name="paymentMethod"
                          id={`payment-${method.code}`}
                          value={method.code}
                          className="sr-only"
                          checked={paymentForm.paymentMethod === method.code}
                          onChange={() => {}}
                          required
                        />
                        <div className="w-16 h-16 mb-2 flex items-center justify-center overflow-hidden">
                          <img 
                            src={method.image} 
                            alt={method.name}
                            className="w-full h-full object-contain" 
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/64?text=Payment";
                            }}
                          />
                        </div>
                        <label 
                          htmlFor={`payment-${method.code}`} 
                          className="text-sm text-center font-medium"
                        >
                          {method.name}
                        </label>
                        {paymentForm.paymentMethod === method.code && (
                          <div className="absolute top-2 right-2">
                            <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                              <div className="w-2 h-2 bg-white rounded-full"></div>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
                  
                  <motion.div variants={itemVariants}>
                    <motion.button
                      type="submit"
                      disabled={isProcessing}
                      className="w-full bg-primary hover:bg-primary-dark text-white font-medium py-3 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-200 flex items-center justify-center"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {isProcessing ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        <>
                          <FaHandHoldingHeart className="mr-2" />
                          Give Now
                        </>
                      )}
                    </motion.button>
                  </motion.div>
                  
                  <motion.p 
                    className="text-sm text-gray-500 text-center mt-4"
                    variants={itemVariants}
                  >
                    Secure payments processed via PayFast
                  </motion.p>
                </form>
              </motion.div>
            </motion.section>
          </motion.div>
        </div>
      </motion.section>
    </motion.main>
  );
}